class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // is-empty also lives on the OUTER <cart-drawer> element (set by Liquid when
    // the cart was empty). innerHTML section swaps never touch outer classes, so
    // clear it here too — otherwise refilling an emptied cart renders the items
    // underneath the empty-state CSS and the drawer looks blank.
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// In-drawer upsell "Add" — delegated at document level so the button keeps
// working after every section re-render (the drawer's inner HTML is replaced
// on each cart update, which would strip any directly-bound listeners).
document.addEventListener('click', function (event) {
  const btn = event.target.closest('[data-cart-upsell-add]');
  if (!btn) return;
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;
  btn.disabled = true;
  btn.textContent = 'Adding…';
  const body = {
    items: [{ id: parseInt(btn.dataset.variantId, 10), quantity: 1 }],
    sections: drawer.getSectionsToRender().map(function (s) { return s.id; }).join(','),
    sections_url: window.location.pathname,
  };
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  })
    .then(function (r) { return r.json(); })
    .then(function (state) {
      if (state.status) throw new Error(state.description || 'Add failed');
      drawer.renderContents(state);
    })
    .catch(function () {
      btn.disabled = false;
      btn.textContent = '+ Add';
    });
});

// Shop Pay express — builds an official cart permalink from the current cart
// lines and jumps straight into the Shop Pay flow. Delegated so it survives
// drawer re-renders.
document.addEventListener('click', function (event) {
  const btn = event.target.closest('[data-shoppay-express]');
  if (!btn) return;
  btn.disabled = true;
  fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      const parts = (cart.items || [])
        .filter(function (i) { return i.variant_id && i.quantity > 0; })
        .map(function (i) { return i.variant_id + ':' + i.quantity; });
      if (!parts.length) { btn.disabled = false; return; }
      window.location.href = '/cart/' + parts.join(',') + '?payment=shop_pay';
    })
    .catch(function () { btn.disabled = false; });
});

// Shipping-protection toggle — adds/removes the protection product and
// re-renders the drawer. Delegated so it survives section re-renders.
document.addEventListener('change', function (event) {
  const toggle = event.target.closest('[data-shipping-protection]');
  if (!toggle) return;
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;
  toggle.disabled = true;
  const sections = drawer.getSectionsToRender().map(function (s) { return s.id; }).join(',');
  const finish = function (state) {
    if (state && !state.status) drawer.renderContents(state);
    toggle.disabled = false;
  };
  const fail = function () { toggle.disabled = false; };
  if (toggle.checked) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(toggle.dataset.variantId, 10), quantity: 1 }],
        sections: sections,
        sections_url: window.location.pathname,
      }),
    }).then(function (r) { return r.json(); }).then(finish).catch(fail);
  } else {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: toggle.dataset.lineKey || String(toggle.dataset.variantId),
        quantity: 0,
        sections: sections,
        sections_url: window.location.pathname,
      }),
    }).then(function (r) { return r.json(); }).then(finish).catch(fail);
  }
});
